/** Production gates are different from the client's journey order. */
export const DFY_BUILD_GATES = ["audit", "plan", "salespage", "webinar", "emails"] as const;
export type DfyBuildGate = (typeof DFY_BUILD_GATES)[number];

export const DFY_GATE_PREREQUISITES: Record<DfyBuildGate, { kind: DfyBuildGate; approved: boolean } | null> = {
  audit: null,
  plan: { kind: "audit", approved: true },
  salespage: { kind: "plan", approved: true },
  webinar: { kind: "salespage", approved: false },
  emails: { kind: "webinar", approved: false }
};

/** The merged strategy launches the audit gate, which produces a plan. */
export function productionGateForStep(step: string): DfyBuildGate | null {
  if (step === "strategy") return "audit";
  return step === "salespage" || step === "webinar" || step === "emails" ? step : null;
}

export interface ProductionWait {
  /** An existing draft needs team work; starting its first build would skip. */
  targetState?: "draft";
  prerequisite: { step: string; state: "missing" | "approval" | "working" | "failed" } | null;
}

/** Availability only: no draft IDs, titles, URLs or content. */
export interface ProductionMaterial {
  kind: string;
  status: string;
  published: boolean;
  /** Staff metadata includes the version so a hidden draft can be the newest. */
  version?: number;
}

/** Match the launcher's EXISTS contract, including an older valid version. */
export function missingProductionPrerequisite(
  gate: DfyBuildGate,
  materials: ReadonlyArray<ProductionMaterial>
): { kind: DfyBuildGate; reason: "missing" | "approval" } | null {
  const prerequisite = DFY_GATE_PREREQUISITES[gate];
  if (!prerequisite) return null;
  const rows = materials.filter((row) => row.kind === prerequisite.kind);
  if (rows.some((row) => !prerequisite.approved || (row.status === "approved" && row.published))) return null;
  return { kind: prerequisite.kind, reason: rows.length ? "approval" : "missing" };
}
